import type { Components, PaletteMode, PaletteOptions, ThemeOptions } from '@mui/material';
import { alpha, Breakpoints, createTheme as createMuiTheme, Palette, responsiveFontSizes, Theme } from '@mui/material';
import { TypographyVariants, TypographyVariantsOptions } from '@mui/material/styles';
import { createBreakpoints, darken, lighten } from '@mui/system';
import { deepmerge } from '@mui/utils';
import { config, ThemeName, themeNames } from 'appConfig';

// Do not change the BASE_FONTS.
// Those fonts are a “system font stack”, it's a fallback to the users OS platform fonts.
// Use THEME_TO_FONT_FAMILY_MAPPING to add custom fonts for each theme.
const BASE_FONTS: string[] = [
  '-apple-system',
  'BlinkMacSystemFont',
  "'Segoe UI'",
  'Oxygen',
  'Ubuntu',
  'Cantarell',
  "'Helvetica Neue'",
  'sans-serif',
];

const THEME_TO_FONT_FAMILY_MAPPING: Record<string, string> = {
  [themeNames.default]: ['Arial', ...BASE_FONTS].join(','),
  [themeNames.leadzai]: ['Arial', ...BASE_FONTS].join(','),
  [themeNames['pag-amrl']]: ['Roboto', ...BASE_FONTS].join(','),
  [themeNames['fcr-be']]: ['Lato', ...BASE_FONTS].join(','),
  [themeNames.youvia]: ['Verdana', ...BASE_FONTS].join(','),
  [themeNames['yp-ca']]: ['Arial', ...BASE_FONTS].join(','),
  [themeNames.pblcrEst]: ['Arial', ...BASE_FONTS].join(','),
  [themeNames.mediamag]: ['Arial', ...BASE_FONTS].join(','),
  [themeNames.medialuo]: ['Arial', ...BASE_FONTS].join(','),
  [themeNames.loclsrch]: ['Arial', ...BASE_FONTS].join(','),
  [themeNames['bee-ddt']]: ['Poppins', ...BASE_FONTS].join(','),
  [themeNames.linkmerc]: ['Arial', ...BASE_FONTS].join(','),
  [themeNames.engacont]: ['Montserrat', ...BASE_FONTS].join(','),
  [themeNames.robin]: ['Poppins', ...BASE_FONTS].join(','),
};

// README: DO NOT USE THIS OBJECT DIRECTLY. FEED THIS TO THE `createPalette` FACTORY FUNCTION
// AND THEN USE THE RETURNED PALETTE OBJECT
export const THEME_TO_PALETTE_OPTIONS_MAPPING: Record<string, PaletteOptions> = {
  [themeNames.default]: {
    primary: {
      main: '#096DD9',
      light: '#48ACFF',
      dark: '#003A8C',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#096DD9',
      light: lighten('#096DD9', 0.08),
      dark: darken('#096DD9', 0.08),
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#2196F3',
      dark: '#0B79D0',
      light: '#64B6F7',
    },
    warning: {
      main: '#FF9800',
      dark: '#C77700',
      light: '#FFB547',
    },
    error: {
      main: '#F44336',
      dark: '#E31B0C',
      light: '#F88078',
    },
    success: {
      main: '#4CAF50',
      dark: '#3B873E',
      light: '#7BC67E',
    },
    action: {
      hoverOpacity: 0.04,
      hover: alpha('#096DD9', 0.04),
    },
    dashboard: {
      firstColor: '#695EDE',
      secondColor: '#36A1AF',
      thirdColor: '#3ABD9F',
      fourthColor: '#9CBBFF',
    },
  },
  [themeNames['pag-amrl']]: {
    primary: {
      main: '#685BC7',
      light: '#B2AAD4',
      dark: '#221E42',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#685BC7',
      light: '#B2AAD4',
      dark: '#221E42',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FAA61A',
    },
    error: {
      main: '#B00020',
      dark: '#E31B0C',
      light: '#F88078',
    },
    action: {
      hover: alpha('#685BC7', 0.04),
      hoverOpacity: 0.04,
    },
    dashboard: {
      firstColor: '#685BC7',
      secondColor: '#E1508C',
      thirdColor: '#ED874D',
      fourthColor: '#BAC75B',
    },
  },
  [themeNames['fcr-be']]: {
    primary: {
      main: '#00ABC2',
      light: '#DDFBFF',
      dark: '#00899C',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#E86877',
      light: '#F3B3BB',
      dark: '#00899C',
    },
    text: {
      primary: '#003A42',
      secondary: '#39676C',
      disabled: '#808080',
    },
    action: {
      hover: alpha('#E86877', 0.04),
      hoverOpacity: 0.04,
    },
    dashboard: {
      firstColor: '#00ABC2',
      secondColor: '#5E9BE2',
      thirdColor: '#C17CC6',
      fourthColor: '#E86877',
    },
  },
  [themeNames['yp-ca']]: {
    primary: {
      main: '#FFCD00',
      light: '#FFE680',
      dark: '#DBB000',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#000000',
      light: '#808080',
      dark: '#DBB000',
      contrastText: '#FFFFFF',
    },
    action: {
      hover: alpha('#FFCD00', 0.1),
      hoverOpacity: 0.04,
    },
    dashboard: {
      firstColor: '#FFCD00',
      secondColor: '#FF8482',
      thirdColor: '#F386EE',
      fourthColor: '#00A2FF',
    },
  },
  [themeNames.youvia]: {
    primary: {
      main: '#FFFF00',
      light: '#43e660',
      dark: '#43e660',
      contrastText: '#000000',
    },
    secondary: {
      main: '#000000',
      light: '#43e660',
      dark: '#43e660',
      contrastText: '#FFFFFF',
    },
    action: {
      hover: alpha('#131D3A', 0.1),
      hoverOpacity: 0.04,
    },
    dashboard: {
      firstColor: '#FFFF00',
      secondColor: '#131D3A',
      thirdColor: '#F8DAD5',
      fourthColor: '#FEFCF8',
    },
  },
  [themeNames.pblcrEst]: {
    primary: {
      main: '#426DA4',
      light: '#779AC8',
      dark: '#2E4B6F',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#426DA4',
      light: '#779AC8',
      dark: '#2E4B6F',
      contrastText: '#FFFFFF',
    },
    dashboard: {
      firstColor: '#695EDE',
      secondColor: '#36A1AF',
      thirdColor: '#3ABD9F',
      fourthColor: '#9CBBFF',
    },
  },
  [themeNames.mediamag]: {
    primary: {
      main: '#E9B83E',
      light: '#F6EDDE',
      dark: '#595447',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#E9B83E',
      light: '#F6EDDE',
      dark: '#595447',
      contrastText: '#FFFFFF',
    },
    dashboard: {
      firstColor: '#E9B83E',
      secondColor: '#595447',
      thirdColor: '#F6EDDE',
      fourthColor: '#7F95AC',
    },
  },
  [themeNames['bee-ddt']]: {
    primary: {
      main: '#801DD4',
      light: '#801DD4',
      dark: '#E95C1DFF',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#E95C1D',
      light: '#E95C1D',
      dark: '#BE4C18FF',
      contrastText: '#FFFFFF',
    },
    dashboard: {
      firstColor: '#E95C1D',
      secondColor: '#801DD4',
      thirdColor: '#FFA425',
      fourthColor: '#06DADE',
    },
  },
  [themeNames.medialuo]: {
    primary: {
      main: '#6423CC',
      light: '#834FD6',
      dark: '#46188E',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#6423CC',
      light: '#834FD6',
      dark: '#46188E',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#2196F3',
      dark: '#0B79D0',
      light: '#64B6F7',
    },
    warning: {
      main: '#FF9800',
      dark: '#C77700',
      light: '#FFB547',
    },
    error: {
      main: '#F44336',
      dark: '#E31B0C',
      light: '#F88078',
    },
    success: {
      main: '#4CAF50',
      dark: '#3B873E',
      light: '#7BC67E',
    },
    action: {
      hoverOpacity: 0.04,
      hover: alpha('#6800CB', 0.04),
    },
    dashboard: {
      firstColor: '#695EDE',
      secondColor: '#36A1AF',
      thirdColor: '#3ABD9F',
      fourthColor: '#9CBBFF',
    },
  },
  [themeNames.loclsrch]: {
    primary: {
      main: '#0073A6',
      light: '#74C7E2',
      dark: '#003A5C',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#003A5C',
      light: '#74C7E2',
      dark: '#0073A6',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#2196F3',
      dark: '#0B79D0',
      light: '#64B6F7',
    },
    warning: {
      main: '#FF9800',
      dark: '#C77700',
      light: '#FFB547',
    },
    error: {
      main: '#F44336',
      dark: '#E31B0C',
      light: '#F88078',
    },
    success: {
      main: '#4CAF50',
      dark: '#3B873E',
      light: '#7BC67E',
    },
    action: {
      hoverOpacity: 0.04,
      hover: alpha('#003A5C', 0.04),
    },
    dashboard: {
      firstColor: '#695EDE',
      secondColor: '#36A1AF',
      thirdColor: '#3ABD9F',
      fourthColor: '#9CBBFF',
    },
  },
  [themeNames.linkmerc]: {
    primary: {
      main: '#0073A6',
      light: '#74C7E2',
      dark: '#003A5C',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#003A5C',
      light: '#74C7E2',
      dark: '#0073A6',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#2196F3',
      dark: '#0B79D0',
      light: '#64B6F7',
    },
    warning: {
      main: '#FF9800',
      dark: '#C77700',
      light: '#FFB547',
    },
    error: {
      main: '#F44336',
      dark: '#E31B0C',
      light: '#F88078',
    },
    success: {
      main: '#4CAF50',
      dark: '#3B873E',
      light: '#7BC67E',
    },
    action: {
      hoverOpacity: 0.04,
      hover: alpha('#003A5C', 0.04),
    },
    dashboard: {
      firstColor: '#695EDE',
      secondColor: '#36A1AF',
      thirdColor: '#3ABD9F',
      fourthColor: '#9CBBFF',
    },
  },
  [themeNames.engacont]: {
    primary: {
      main: '#eaefec',
      light: '#949594',
      dark: '#5A5B5B',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#202221',
      light: '#949594',
      dark: '#5A5B5B',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#2196F3',
      dark: '#0B79D0',
      light: '#64B6F7',
    },
    warning: {
      main: '#FF9800',
      dark: '#C77700',
      light: '#FFB547',
    },
    error: {
      main: '#F44336',
      dark: '#E31B0C',
      light: '#F88078',
    },
    success: {
      main: '#4CAF50',
      dark: '#3B873E',
      light: '#7BC67E',
    },
    action: {
      hoverOpacity: 0.04,
      hover: alpha('#202221', 0.04),
    },
    dashboard: {
      firstColor: '#695EDE',
      secondColor: '#36A1AF',
      thirdColor: '#3ABD9F',
      fourthColor: '#9CBBFF',
    },
  },
  [themeNames.robin]: {
    primary: {
      main: '#14332d',
      light: '#48ACFF',
      dark: '#003A8C',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#14332d',
      light: lighten('#096DD9', 0.08),
      dark: darken('#096DD9', 0.08),
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#2196F3',
      dark: '#0B79D0',
      light: '#64B6F7',
    },
    warning: {
      main: '#FF9800',
      dark: '#C77700',
      light: '#FFB547',
    },
    error: {
      main: '#F44336',
      dark: '#E31B0C',
      light: '#F88078',
    },
    success: {
      main: '#4CAF50',
      dark: '#3B873E',
      light: '#7BC67E',
    },
    action: {
      hoverOpacity: 0.04,
      hover: alpha('#14332d', 0.04),
    },
    dashboard: {
      firstColor: '#695EDE',
      secondColor: '#36A1AF',
      thirdColor: '#3ABD9F',
      fourthColor: '#9CBBFF',
    },
  },
};

export const TYPOGRAPHY_OPTIONS: TypographyVariantsOptions = {
  fontFamily: THEME_TO_FONT_FAMILY_MAPPING[config.theme],
  h4: {
    fontWeight: 700,
  },
  h6: {
    fontWeight: 700,
    lineHeight: 1.1,
  },
  subtitle2: {
    lineHeight: 1.5,
  },
  button: {
    textTransform: 'none',
    lineHeight: 1.714,
  },
  caption: {
    lineHeight: 1.17167,
  },
  overline: {
    lineHeight: 2.583,
  },
};

export function createThemeOptions(
  palette: Palette,
  typography: TypographyVariants,
  breakpoints: Breakpoints,
): ThemeOptions {
  const baseComponentsSettings: Components = {
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            backgroundColor: 'unset',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        color: 'secondary',
      },
      styleOverrides: {
        root: {
          padding: '6px 14px',
          '&.Mui-disabled': {
            cursor: 'not-allowed',
          },
          '&:disabled': {
            backgroundColor: 'action.disabled',
            color: '#00000050',
            cursor: 'not-allowed',
            'p, span': { color: '#00000070' },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        indeterminate: {
          color: palette.primary.main,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paperAnchorRight: {
          width: '30vw',
          [breakpoints.down('lg')]: {
            width: '35vw',
          },
          [breakpoints.down('md')]: {
            width: '45vw',
          },
          [breakpoints.down('sm')]: {
            width: '65vw',
          },
          [breakpoints.down('xs')]: {
            width: '100vw',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&$error': {
            color: palette.text.secondary,
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          minHeight: 'max-content',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.MuiTouchRipple-root': {
            color: palette.secondary.main,
          },
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        anchorOrigin: {
          horizontal: 'center',
          vertical: 'bottom',
        },
        transformOrigin: {
          horizontal: 'center',
          vertical: 'top',
        },
        TransitionProps: {
          mountOnEnter: true,
        },
        PaperProps: {
          style: {
            minWidth: 175,
            boxShadow: '2px 8px 16px 0px rgba(24, 50, 115, 0.20)',
          },
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        anchorOrigin: {
          horizontal: 'center',
          vertical: 'bottom',
        },
        transformOrigin: {
          horizontal: 'center',
          vertical: 'top',
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        color: 'primary',
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          anchorOrigin: {
            horizontal: 'center',
            vertical: 'bottom',
          },
          transformOrigin: {
            horizontal: 'center',
            vertical: 'top',
          },
        },
      },
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            cursor: 'not-allowed',
          },
        },
        label: {
          '&.MuiStepLabel-completed': {
            fontWeight: typography.fontWeightRegular,
            '&.MuiStepLabel-active': {
              color: palette.primary.main,
              fontWeight: typography.fontWeightMedium,
            },
          },
          '&.MuiStepLabel-active': {
            color: palette.primary.main,
            fontWeight: typography.fontWeightMedium,
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        color: 'primary',
      },
      styleOverrides: {
        switchBase: {
          color: palette.mode === 'light' ? '#FAFAFA' : undefined,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          // same font size and weight as h6
          fontSize: typography.h6.fontSize,
          fontWeight: typography.h6.fontWeight,
          '&&.Mui-selected': {
            color: palette.text.primary,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: '4px',
          borderRadius: '8px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: 5,
          marginLeft: 2,
          marginRight: 2,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        action: {
          alignSelf: 'center',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        SelectProps: {
          MenuProps: {
            anchorOrigin: {
              horizontal: 'center',
              vertical: 'bottom',
            },
            transformOrigin: {
              horizontal: 'center',
              vertical: 'top',
            },
          },
        },
      },
      styleOverrides: {
        root: {
          input: {
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 30px ${palette.background.paper} inset`,
            },
          },
        },
      },
    },
    // TODO: This shouldn't be set, as it overrides the text color for components such as Alerts.
    MuiTypography: {
      defaultProps: {
        color: 'textPrimary',
      },
    },
  };

  const appThemeToComponentsSettingsMap: PartialRecord<ThemeName, Components> = {
    'yp-ca': {
      MuiButton: {
        styleOverrides: {
          containedSecondary: {
            color: palette.secondary.main,
            backgroundColor: palette.primary.main,
            '&.MuiTouchRipple-root': {
              color: palette.secondary.main,
            },
            '&:hover': {
              backgroundColor: palette.primary.main,
            },
          },
          outlinedSecondary: {
            border: `1px solid ${palette.primary.main}`,
            color: palette.mode === 'light' ? palette.secondary.main : palette.primary.main,
            '&.MuiTouchRipple-root': {
              color: palette.primary.main,
            },
            '&:hover': {
              border: `1px solid ${palette.primary.main}`,
              backgroundColor: palette.action.hover,
            },
          },
          textSecondary: {
            color: palette.mode === 'light' ? palette.secondary.main : palette.primary.main,
            '&:hover': {
              backgroundColor:
                palette.mode === 'light' ? alpha(palette.secondary.main, 0.1) : alpha(palette.primary.main, 0.1),
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: palette.secondary.main,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            // WIP: not sure if this will work on root styles
            // colorPrimary and colorSecondary were deprecated in v5
            color: palette.mode === 'light' ? palette.secondary.main : palette.primary.main,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            '&.MuiTouchRipple-root': {
              color: palette.primary.main,
            },
          },
          outlinedPrimary: {
            color: palette.mode === 'light' ? palette.secondary.main : palette.primary.main,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.MuiTouchRipple-root': {
              color: palette.primary.main,
            },
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          colorPrimary: {
            color: palette.mode === 'light' ? palette.secondary.main : palette.primary.main,
          },
          colorSecondary: {
            color: palette.mode === 'light' ? palette.secondary.main : palette.primary.main,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: {
            color: palette.secondary.main,
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            '&.MuiStepLabel-completed': {
              '&.MuiStepLabel-active': {
                color: palette.mode === 'light' ? palette.secondary.main : palette.primary.main,
              },
            },
            '&.MuiStepLabel-active': {
              color: palette.mode === 'light' ? palette.secondary.main : palette.primary.main,
            },
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            '&.Mui-active': {
              '&.MuiStepIcon-text': {
                fill: palette.secondary.main,
              },
            },
          },
        },
      },
    },
    youvia: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#FFFFFF',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: palette.primary.contrastText,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedSecondary: {
            fontWeight: 700,
            color: palette.primary.contrastText,
            backgroundColor: palette.primary.main,
            '&.MuiTouchRipple-root': {
              color: palette.primary.light,
            },
            '&:hover': {
              backgroundColor: palette.primary.light,
            },
          },
          outlinedSecondary: {
            border: `1px solid ${palette.secondary.main}`,
            backgroundColor: palette.secondary.main,
            color: palette.secondary.contrastText,
            '&.MuiTouchRipple-root': {
              color: palette.secondary.contrastText,
            },
            '&:hover': {
              border: `1px solid ${palette.secondary.light}`,
              backgroundColor: palette.secondary.light,
              color: palette.secondary.main,
            },
          },
          textSecondary: {
            color: palette.primary.contrastText,
            border: `1px solid ${palette.primary.contrastText}`,
            '&:hover': {
              backgroundColor: palette.primary.light,
              color: palette.primary.contrastText,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: palette.primary.main,
            '&.MuiChip-outlined': {
              border: `1px solid ${palette.secondary.main}`,
              color: palette.secondary.main,
            },
            '&.MuiChip-deleteIcon': {
              color: palette.secondary.main,
            },
            '&.MuiTouchRipple-root': {
              color: palette.secondary.main,
            },
          },
          outlinedPrimary: {
            color: palette.mode === 'light' ? palette.secondary.main : palette.primary.main,
          },
        },
      },
    },
    mediamag: {
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: 'white',
          },
          root: {
            color: palette.text.primary,
            borderBottom: `1px solid ${palette.grey['200']}`,
          },
        },
      },
    },
    robin: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            '--AppBar-background': '#dffe5b',
            '--AppBar-color': '#14332d',
            backgroundColor: '#dffe5b',
            color: '#14332d',
          },
        },
      },
    },
  };

  const componentCurrentThemeSettings = appThemeToComponentsSettingsMap[config.theme] ?? {};
  const components = deepmerge(baseComponentsSettings, componentCurrentThemeSettings);

  return {
    palette,
    breakpoints,
    typography,
    components,
  };
}

/**
 * Creates a MUI theme
 * @param paletteMode type PaletteMode = "light" | "dark"
 * @param additionalOptions any additional option when creating a Mui Theme
 * @param userTheme In case you want to override the default theme defined in the env vars
 * @returns {Theme}
 */
export function createTheme(paletteMode: PaletteMode, userTheme: ThemeName, additionalOptions: object[] = []): Theme {
  const baseTheme = createMuiTheme({
    palette: {
      mode: paletteMode,
      background: { default: '#F8F9FB' },
      dataGrid: {
        cellBorderRight: '#F0F0F0',
        cellBorderBottom: '#EFEFEF',
        headerBorderRight: '#D9D9D9',
        rowHover: '#F9F9F9',
      },
      ...THEME_TO_PALETTE_OPTIONS_MAPPING[userTheme],
    },
    typography: TYPOGRAPHY_OPTIONS,
  });

  const { palette } = baseTheme;
  const { typography } = baseTheme;

  const breakpoints = createBreakpoints({
    values: {
      xs: 0,
      sm: 767,
      md: 1044,
      lg: 1280,
      xl: 1920,
    },
  });

  const themeOptions = createThemeOptions(palette, typography, breakpoints);
  const muiTheme = createMuiTheme(themeOptions, ...additionalOptions);
  const responsiveTypographyTheme = responsiveFontSizes(muiTheme);

  return responsiveTypographyTheme;
}
